import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowRight,
  ChevronRight,
  Download,
  Info,
  Upload,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Cabecera } from "@/components/propuesta/cabecera";
import { type FiltroElegido, Filtros } from "@/components/propuesta/filtros";
import {
  Aviso,
  Caja,
  Estado,
  Pista,
  Punto,
  Sparkline,
  Tile,
} from "@/components/propuesta/piezas";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { mesEnLetras, mesSuelto } from "@/lib/periodo";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/")({ component: Inicio });

type Bandeja = {
  nombre: string;
  tono: "ok" | "warn" | "bad";
  cuantos: number;
  porcentaje: number;
  explicacion: string;
};
type Empresa = {
  empresa: string;
  hechos: number;
  total: number;
  porcentaje: number;
};
type Actividad = {
  texto: string;
  hora: string;
  tono: "ok" | "warn" | "bad" | "acc" | "neutro";
};
type AvisoDato = {
  titulo: string;
  detalle: string;
  gravedad: "error" | "aviso";
};

// ===========================================================================
// El arranque, siguiendo `propuesta-diseno/pantalla-1-inicio.html`.
//
// Dos secciones, como la lámina: arriba «cómo va el cierre» en tiles con su
// cinta de color, y abajo «lo que está esperando por alguien» en tres cajas —
// las bandejas, el avance por empresa y lo que ha pasado.
//
// Todo con datos de la base. Donde todavía no hay nada que contar se dice, en
// vez de rellenar con una cifra de ejemplo: un cero explicado informa; un 18
// inventado, no.
// ===========================================================================

function Inicio() {
  // Sobre qué dinero se está mirando. Contabilidad lo pidió por grupo de razón
  // social, cuenta y moneda: la portada era una cifra única del mes entero.
  const [filtro, setFiltro] = useState<FiltroElegido>({});
  const { data, isPending, error } = api.inicio.useQuery(
    [
      "inicio",
      filtro.empresaId ?? "",
      filtro.cuentaId ?? "",
      filtro.moneda ?? "",
    ],
    { query: filtro },
  );
  const d = data?.status === 200 ? data.body : null;

  // «Procesar lo pendiente» es volver a cruzar el periodo entero con lo que se
  // haya corregido a mano desde la última pasada. El botón llevaba desde el
  // principio sin `onClick`: se pintaba habilitado y no hacía nada, y es el que
  // hizo que contabilidad dejara de fiarse del resto de la pantalla.
  const qc = useQueryClient();
  const recruzar = api.recruzar.useMutation();
  const procesarPendiente = () => {
    if (recruzar.isPending) return;
    recruzar.mutate(
      { body: { confirmo: true } },
      {
        onSuccess: (r: {
          status: number;
          body: { dice?: string; message?: string };
        }) => {
          if (r.status !== 200) {
            toast.error(r.body.message ?? "No se pudo volver a cruzar");
            return;
          }
          toast.success(r.body.dice ?? "Cruzado de nuevo");
          qc.invalidateQueries();
        },
        onError: () => toast.error("No se pudo volver a cruzar"),
      },
    );
  };

  // Qué vista se abre por defecto es una decisión de quien mira, no del
  // sistema: gerencia quiere el número y contabilidad quiere el detalle. Se
  // recuerda en el navegador para no tener que elegir cada mañana.
  const [vista, setVista] = useState<"sencilla" | "completa">(
    () =>
      (localStorage.getItem("inicio.vista") as "sencilla" | "completa") ??
      "completa",
  );
  const cambiarVista = (v: "sencilla" | "completa") => {
    setVista(v);
    localStorage.setItem("inicio.vista", v);
  };

  if (isPending) {
    return (
      <>
        <Cabecera titulo="Inicio" />
        <div className="grid gap-3.5 sm:grid-cols-2 lg:grid-cols-5">
          {["a", "b", "c", "d", "e"].map((k) => (
            <Skeleton key={k} className="h-32" />
          ))}
        </div>
        <div className="grid gap-3.5 lg:grid-cols-3">
          {["x", "y", "z"].map((k) => (
            <Skeleton key={k} className="h-72" />
          ))}
        </div>
      </>
    );
  }

  if (error || !d) {
    return (
      <>
        <Cabecera titulo="Inicio" />
        <Aviso tono="bad">
          <AlertTriangle className="mt-px size-4 shrink-0" />
          No se pudo leer el estado del módulo. Revisá que el servidor esté
          levantado.
        </Aviso>
      </>
    );
  }

  const n = (v: number) => v.toLocaleString("es-PE");
  const soles = (centimos: number) =>
    `S/ ${(centimos / 100).toLocaleString("es-PE", { maximumFractionDigits: 0 })}`;

  return (
    <>
      <Cabecera
        titulo="Inicio"
        // Sin `marcador`: la portada todavía no filtra nada, y la caja que
        // salía aquí decía «Filtrar lo que se ve en esta pantalla…» y estaba
        // deshabilitada. La faceta del periodo sí se queda: es informativa.
        facetas={
          vista === "completa" ? [{ clave: "Periodo", valor: d.periodo }] : []
        }
        acciones={
          <>
            <Conmutador vista={vista} alCambiar={cambiarVista} />
            {vista === "completa" && (
              <>
                <Button
                  size="sm"
                  disabled={recruzar.isPending}
                  onClick={procesarPendiente}
                >
                  <ArrowRight className="mr-2 size-4" />
                  {recruzar.isPending ? "Cruzando…" : "Procesar lo pendiente"}
                </Button>
                <Button size="sm" variant="outline" asChild>
                  <Link to="/carga">
                    <Upload className="mr-2 size-4" />
                    Cargar PDFs
                  </Link>
                </Button>
                <Button size="sm" variant="outline" asChild>
                  <a href="/exportar/cierre.csv" download>
                    <Download className="mr-2 size-4" />
                    Exportar el cierre
                  </a>
                </Button>
              </>
            )}
          </>
        }
      />

      {vista === "sencilla" ? (
        <Sencilla d={d} n={n} alCambiar={cambiarVista} />
      ) : (
        <>
          {/* ---- Cómo va el cierre ------------------------------------------ */}
          <section className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-semibold text-muted-foreground text-xs uppercase tracking-wider">
                Cómo va el cierre de {mesEnLetras(d.periodo)}
              </h2>
              {/* Sobre qué dinero se está mirando. Toca TODAS las cifras de
                  abajo: una portada donde el filtro afecta a unas y no a otras
                  es peor que una sin filtro, porque nadie sabe cuáles mira. */}
              <Filtros
                opciones={d.opciones}
                valor={filtro}
                alCambiar={setFiltro}
              />
            </div>
            <div className="grid gap-3.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
              <Tile
                titulo="Conciliado"
                subtitulo="del importe abonado"
                valor={d.conciliado.porcentaje.toLocaleString("es-PE")}
                unidad="%"
                tono="ok"
                pie={`${soles(d.conciliado.importe)} de ${soles(d.conciliado.total)}`}
              >
                <Pista porcentaje={d.conciliado.porcentaje} tono="ok" />
              </Tile>

              {/* LOS DOS QUE PIDIÓ CONTABILIDAD.
                  «Conciliado» de arriba mide importe sobre importe y no
                  distingue si hay papel detrás. Esto cuenta lo que el área
                  llama conciliado: el movimiento con su documento. */}
              <Tile
                titulo="Conciliados con documento"
                subtitulo="con su voucher y su boleta"
                valor={n(d.indicadores.conciliados)}
                tono={d.indicadores.conciliados > 0 ? "ok" : "neutro"}
              />

              <Tile
                titulo="Sin documento"
                subtitulo="ningún voucher los explica"
                valor={n(d.indicadores.sinDocumento)}
                tono={d.indicadores.sinDocumento > 0 ? "bad" : "ok"}
                pie={
                  d.indicadores.sinDocumento > 0
                    ? "falta subir su expediente, o no son cobros de boleta"
                    : undefined
                }
              />

              <Tile
                titulo="Necesita una persona"
                subtitulo="bandeja 3 · revisión"
                valor={n(d.necesitaPersona)}
                tono="warn"
                pie={
                  <>
                    <Info className="size-3" />
                    ninguno se cerró sin evidencia suficiente
                  </>
                }
              />

              <Tile
                titulo="Préstamos entre empresas"
                subtitulo="detectados por número de cuenta"
                valor={n(d.prestamosEntreEmpresas)}
                tono="loan"
                pie={
                  d.prestamosEntreEmpresas === 0
                    ? "el cruce no encontró ninguno"
                    : "entre cuentas del propio grupo"
                }
              />

              <Tile
                titulo="Abonos sin respaldo"
                subtitulo="dinero que entró sin voucher"
                valor={n(d.sinRespaldo.cuantos)}
                tono="bad"
                pie={
                  d.sinRespaldo.cuantos > 0
                    ? soles(d.sinRespaldo.importe)
                    : "ninguno todavía"
                }
              />

              <Tile
                titulo="Vouchers leídos por día"
                subtitulo={`${n(d.adjuntos)} adjuntos → ${n(d.imagenesDistintas)} imágenes distintas`}
                tono="acc"
                ancho={2}
                pie="el mismo voucher llega adjunto a varias boletas; se lee una vez"
              >
                <Sparkline serie={d.vouchersPorDia} />
              </Tile>
            </div>

            {/* CUÁNTO CUESTA LEER, y qué falta para poder medir si mejora.
                Contabilidad pidió ver «aciertos, cuánto se corrige a mano y
                coste por pasada». Esto es la parte que hoy tiene datos: el
                reparto entre el lector local —que no se cobra— y el modelo, y
                lo que se ha gastado. Los aciertos necesitan que alguien
                verifique vouchers, y ese contador va aquí justamente para que
                se vea en cero: mientras lo esté, no hay con qué medir. */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border bg-muted/30 px-4 py-3 text-[12.5px]">
              <span className="font-medium text-muted-foreground">
                La lectura
              </span>
              <span>
                <b className="tabular-nums">{n(d.lectura.leidas)}</b> imágenes
                leídas
              </span>
              <span className="text-muted-foreground">
                <b className="text-foreground tabular-nums">
                  {n(d.lectura.porOptico)}
                </b>{" "}
                por el lector local ·{" "}
                <b className="text-foreground tabular-nums">
                  {n(d.lectura.porModelo)}
                </b>{" "}
                por el modelo
              </span>
              <span className="text-muted-foreground">
                gastado{" "}
                <b className="text-foreground tabular-nums">
                  ${d.lectura.costo.toFixed(3)}
                </b>
                {d.lectura.conCosto < d.lectura.porModelo ? (
                  <span className="ml-1">
                    (de {n(d.lectura.conCosto)} con coste anotado)
                  </span>
                ) : null}
              </span>
              <span
                className={
                  d.lectura.verificadas === 0
                    ? "ml-auto rounded border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-amber-700 dark:text-amber-400"
                    : "ml-auto text-muted-foreground"
                }
              >
                {d.lectura.verificadas === 0
                  ? "sin verificar ni un voucher: no hay con qué medir si el lector mejora"
                  : `${n(d.lectura.verificadas)} vouchers verificados a mano`}
              </span>
            </div>
          </section>

          {/* ---- Lo que espera por alguien ---------------------------------- */}
          <section className="space-y-3">
            <h2 className="font-semibold text-muted-foreground text-xs uppercase tracking-wider">
              Lo que está esperando por alguien
            </h2>
            <div className="grid gap-3.5 lg:grid-cols-3">
              <Caja
                titulo="Las tres bandejas"
                derecha={`${n(d.totalPagos)} abonos`}
              >
                <div className="flex h-full flex-col gap-4">
                  {(d.bandejas as Bandeja[]).map((b) => (
                    <div key={b.nombre}>
                      <div className="flex items-baseline gap-2">
                        <Estado tono={b.tono}>{b.nombre}</Estado>
                        <b className="ml-auto text-[19px] tabular-nums">
                          {n(b.cuantos)}
                        </b>
                      </div>
                      <Pista porcentaje={b.porcentaje} tono={b.tono} />
                      <p className="mt-1.5 text-[11.5px] text-muted-foreground">
                        {b.explicacion}
                      </p>
                    </div>
                  ))}
                  <div className="mt-auto pt-2">
                    <Aviso tono="info">
                      <Info className="mt-px size-4 shrink-0" />
                      El sistema nunca cierra en silencio con evidencia floja.
                    </Aviso>
                  </div>
                </div>
              </Caja>

              <Caja
                titulo="Avance por empresa"
                derecha={`${d.empresasCargadas} de ${d.empresasTotales} con movimientos`}
              >
                <div className="space-y-0">
                  {(d.porEmpresa as Empresa[]).length === 0 ? (
                    // Con el periodo vacío la caja quedaba con su título y nada
                    // debajo, que se lee como una tabla que no cargó.
                    <p className="py-1 text-[12.5px] text-muted-foreground">
                      Ninguna de las {n(d.empresasTotales)} empresas tiene
                      movimientos todavía. Aparecen acá en cuanto se carga el
                      estado de cuenta de alguna de sus cuentas.
                    </p>
                  ) : null}
                  {(d.porEmpresa as Empresa[]).map((e) => (
                    <div
                      key={e.empresa}
                      className="border-b py-2.5 last:border-0"
                    >
                      <div className="flex items-baseline gap-2">
                        <b className="truncate text-[12.5px]" title={e.empresa}>
                          {e.empresa}
                        </b>
                        <span className="ml-auto shrink-0 text-[12px] text-muted-foreground tabular-nums">
                          {n(e.hechos)} / {n(e.total)}
                        </span>
                      </div>
                      <Pista
                        porcentaje={e.porcentaje}
                        tono={
                          e.porcentaje >= 95
                            ? "ok"
                            : e.porcentaje >= 80
                              ? "acc"
                              : "bad"
                        }
                      />
                    </div>
                  ))}
                  {(d.avisos as AvisoDato[]).map((a) => (
                    <div key={a.detalle} className="mt-3">
                      <Aviso tono="warn">
                        <AlertTriangle className="mt-px size-4 shrink-0" />
                        <span>
                          <b>{a.titulo}</b> — {a.detalle}
                        </span>
                      </Aviso>
                    </div>
                  ))}
                </div>
              </Caja>

              <Caja titulo="Qué ha pasado" derecha="del registro del pipeline">
                <div className="space-y-2.5">
                  {(d.actividad as Actividad[]).length === 0 ? (
                    <p className="text-[12.5px] text-muted-foreground">
                      Todavía no ha pasado nada en este periodo. Acá van
                      apareciendo los archivos según se leen y los cruces según
                      se deciden.
                    </p>
                  ) : (
                    (d.actividad as Actividad[]).map((a) => (
                      <div
                        key={`${a.texto}-${a.hora}`}
                        className="flex items-start gap-2.5 text-[12.5px]"
                      >
                        <Punto tono={a.tono} />
                        <span className="flex-1">{a.texto}</span>
                        <span className="shrink-0 text-[11px] text-muted-foreground tabular-nums">
                          {a.hora}
                        </span>
                      </div>
                    ))
                  )}

                  <div className="mt-3 border-t pt-3">
                    <div className="mb-2 font-bold text-[11px] text-muted-foreground uppercase tracking-wider">
                      Siguiente paso sugerido
                    </div>
                    <div className="flex items-center gap-2.5">
                      <div className="grid size-[34px] shrink-0 place-items-center rounded-lg border border-amber-500/40 bg-amber-500/10 text-amber-600">
                        <AlertTriangle className="size-4" />
                      </div>
                      {/* Los dos números, separados. Antes decía «revisar los
                          1.898: el cruce no encontró boleta», y para 77 de
                          ellos sí la encontró — son los únicos que la revisión
                          guiada puede preguntar. Mandar a la mesa entera a
                          quien puede cerrar 77 en un rato es enterrarlos.

                          Y con el periodo vacío decía «revisar los 0 abonos con
                          propuesta» y mandaba a una mesa sin filas: el primer
                          paso de un mes recién abierto es cargarlo. */}
                      <p className="text-[12.5px] text-muted-foreground">
                        {d.totalPagos === 0 ? (
                          <>
                            Cargar los{" "}
                            <b className="text-foreground">
                              estados de cuenta del mes
                            </b>
                            : hasta que entre el primero no hay movimientos que
                            conciliar ni nada que decidir.
                          </>
                        ) : (
                          <>
                            Revisar los{" "}
                            <b className="text-foreground">
                              {n(d.esperanDecision)} abonos con propuesta
                            </b>
                            : el cruce les encontró boleta y falta que alguien
                            lo confirme. Los otros{" "}
                            {n(
                              Math.max(
                                0,
                                d.necesitaPersona - d.esperanDecision,
                              ),
                            )}{" "}
                            siguen sin un voucher que los explique.
                          </>
                        )}
                      </p>
                    </div>
                    <Button
                      asChild
                      className="mt-3 w-full bg-slate-900 text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
                    >
                      <Link
                        to={
                          d.totalPagos === 0
                            ? "/carga"
                            : d.esperanDecision > 0
                              ? "/revision"
                              : "/movimientos"
                        }
                      >
                        {d.totalPagos === 0
                          ? "Cargar el mes"
                          : d.esperanDecision > 0
                            ? "Empezar la revisión guiada"
                            : "Abrir la mesa de trabajo"}
                      </Link>
                    </Button>
                  </div>
                </div>
              </Caja>
            </div>
          </section>
        </>
      )}
    </>
  );
}

// ===========================================================================
// La vista sencilla (lámina 7).
//
// La misma verdad que la completa, dicha en una frase. Es la pantalla que se
// abre cuando alguien entra sin querer entender el sistema: cuántos casos
// esperan, cuánto se tarda y por dónde se empieza.
//
// LAS DOS CIFRAS SON DISTINTAS Y HAY QUE DECIRLO.
//
// Aquí se enseñaba `esperanDecision` como cifra grande y se restaba del total
// para contar lo cerrado: «de los 2.089 pagos, el sistema cerró 2.012 sin
// preguntar nada». Era falso por dos vueltas de tuerca:
//
//   necesitaPersona   1.898  ← TODO lo que sigue pendiente
//   esperanDecision      77  ← solo lo pendiente QUE ADEMÁS tiene candidato
//   conciliado.cuantos  191  ← lo que el sistema cerró de verdad
//
// `esperanDecision` sale de `colaDeRevision`, que exige `exists(candidato)`.
// Los 1.821 abonos pendientes sin ni un voucher que los explique no entraban
// en la cifra, y la resta los daba por cerrados. Gerencia abría la portada y
// leía que el mes estaba hecho cuando iba por el 9 %.
//
// Ahora la cifra grande es la honesta —lo que falta— y las dos de debajo la
// desglosan: cuántas tienen ya una propuesta que mirar y cuántas ni eso. La
// estimación de minutos sigue contra `esperanDecision`, porque son las únicas
// que la revisión guiada puede preguntar de una en una.
// ===========================================================================

function Conmutador({
  vista,
  alCambiar,
}: {
  vista: "sencilla" | "completa";
  alCambiar: (v: "sencilla" | "completa") => void;
}) {
  return (
    <div className="mr-1 flex rounded-md border p-0.5">
      {(["sencilla", "completa"] as const).map((v) => (
        <button
          key={v}
          type="button"
          onClick={() => alCambiar(v)}
          className={cn(
            "rounded px-2.5 py-1 font-medium text-[12px] transition-colors",
            vista === v
              ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          Vista {v}
        </button>
      ))}
    </div>
  );
}

function Sencilla({
  d,
  n,
  alCambiar,
}: {
  alCambiar: (v: "sencilla" | "completa") => void;
  d: {
    periodo: string;
    esperanDecision: number;
    necesitaPersona: number;
    totalPagos: number;
    conciliado: { porcentaje: number; cuantos: number };
    prestamosEntreEmpresas: number;
    sinRespaldo: { cuantos: number };
    adjuntos: number;
    avisos: AvisoDato[];
  };
  n: (v: number) => string;
}) {
  // Los pendientes que la revisión guiada NO puede preguntar, porque no tiene
  // ningún voucher que ponerles al lado. Son la mayor parte, y callarlos era
  // justo lo que hacía que la portada pareciera un mes casi cerrado.
  const sinPropuesta = Math.max(0, d.necesitaPersona - d.esperanDecision);
  const minutos = Math.max(1, Math.round(d.esperanDecision / 5));

  // El mes recién abierto, que hasta ahora nadie había visto.
  //
  // Con la base en cero la frase de abajo salía así: «De los 0 abonos de enero,
  // el sistema pudo cerrar 0 por su cuenta. Los 0 que quedan tienen ya una
  // propuesta que mirar» — un cero grande, tres ceros más y una afirmación
  // sobre nada. La portada es lo primero que se abre el día que se estrena un
  // periodo, y lo que hace falta ahí no es un balance: es por dónde se empieza.
  if (d.totalPagos === 0) {
    return (
      <div className="mx-auto w-full max-w-2xl py-16 text-center">
        <h2 className="font-semibold text-3xl tracking-tight">
          {mesEnLetras(d.periodo)} está recién abierto
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-[15px] text-muted-foreground">
          Todavía no se cargó ningún estado de cuenta, así que no hay
          movimientos que conciliar. Se empieza subiendo los PDF que mandó el
          banco: el sistema detecta a qué cuenta y a qué moneda pertenece cada
          uno, y deja corregirlo antes de meterlo.
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Button size="lg" className="bg-cyan-600 hover:bg-cyan-700" asChild>
            <Link to="/carga">
              <ArrowRight className="mr-2 size-4" />
              Cargar los estados de cuenta
            </Link>
          </Button>
          <Button size="lg" variant="outline" asChild>
            <Link to="/empresas">Ver las cuentas del grupo</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-3xl py-10 text-center">
      <div className="font-bold text-[96px] text-amber-600 leading-none tracking-tight dark:text-amber-500">
        {n(d.necesitaPersona)}
      </div>
      <h2 className="mt-2 font-semibold text-3xl tracking-tight">
        movimientos esperan tu decisión
      </h2>
      <p className="mx-auto mt-4 max-w-2xl text-[15px] text-muted-foreground">
        De los {n(d.totalPagos)} abonos de {mesSuelto(d.periodo)}, el sistema
        pudo cerrar {n(d.conciliado.cuantos)} por su cuenta.{" "}
        {sinPropuesta > 0 ? (
          <>
            De los {n(d.necesitaPersona)} que quedan,{" "}
            <b className="text-foreground">{n(d.esperanDecision)}</b> ya tienen
            una propuesta que mirar y{" "}
            <b className="text-foreground">{n(sinPropuesta)}</b> no tienen
            todavía ningún voucher que los explique.
          </>
        ) : (
          <>
            Los {n(d.necesitaPersona)} que quedan tienen ya una propuesta que
            mirar.
          </>
        )}
      </p>

      <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
        <Button
          size="lg"
          className="bg-cyan-600 hover:bg-cyan-700"
          disabled={d.esperanDecision === 0}
          asChild={d.esperanDecision > 0}
        >
          {d.esperanDecision > 0 ? (
            <Link to="/revision">
              <ArrowRight className="mr-2 size-4" />
              Revisar los {n(d.esperanDecision)} con propuesta
            </Link>
          ) : (
            <span>
              <ArrowRight className="mr-2 size-4" />
              No hay ninguno con propuesta
            </span>
          )}
        </Button>
        <Button size="lg" variant="outline" asChild>
          <Link to="/movimientos">Verlos todos en una lista</Link>
        </Button>
      </div>
      <p className="mt-3 text-[12.5px] text-muted-foreground">
        {d.esperanDecision > 0 ? (
          <>Se revisan de uno en uno · unos {n(minutos)} minutos en total</>
        ) : null}
        {sinPropuesta > 0 ? (
          <>
            {d.esperanDecision > 0 ? " · " : ""}
            los otros {n(sinPropuesta)} necesitan que se cargue su documento
          </>
        ) : null}
      </p>

      {/* Las cuatro cifras que alguien de gerencia sí quiere ver. */}
      <div className="mt-9 flex flex-wrap items-stretch justify-center divide-x">
        {[
          {
            v: `${d.conciliado.porcentaje.toLocaleString("es-PE")} %`,
            t: "del dinero ya conciliado",
            c: "text-emerald-600 dark:text-emerald-500",
          },
          {
            v: n(d.prestamosEntreEmpresas),
            t: "préstamos entre empresas",
            c: "text-blue-600 dark:text-blue-500",
          },
          {
            v: n(d.sinRespaldo.cuantos),
            t: "abonos sin voucher",
            c: "text-red-600 dark:text-red-500",
          },
          { v: n(d.adjuntos), t: "archivos leídos", c: "text-foreground" },
        ].map((c) => (
          <div key={c.t} className="px-7">
            <div className={cn("font-bold text-2xl tabular-nums", c.c)}>
              {c.v}
            </div>
            <div className="mt-1 text-[12px] text-muted-foreground">{c.t}</div>
          </div>
        ))}
      </div>

      {d.avisos.length > 0 && (
        <div className="mx-auto mt-8 max-w-2xl space-y-2 text-left">
          {d.avisos.slice(0, 2).map((a) => (
            <Aviso
              key={a.titulo}
              tono={a.gravedad === "error" ? "bad" : "warn"}
            >
              <AlertTriangle className="mt-px size-4 shrink-0" />
              <span>
                <b>{a.titulo}</b> — {a.detalle}
              </span>
            </Aviso>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={() => alCambiar("completa")}
        className="mx-auto mt-3 flex w-full max-w-2xl items-center gap-2 rounded-lg border border-dashed p-3 text-left text-[12.5px] text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
      >
        <ChevronRight className="size-3.5 shrink-0" />
        Ver el detalle completo: avance por empresa, actividad del día y las
        tres bandejas
      </button>
    </div>
  );
}
